import {
  IncentiveCategory,
  IncentiveLifecycleStatus,
  IncentiveStatus,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { resolveIncentiveContent } from 'features/incentive/content/resolveIncentiveContent';
import { formatEntryCount } from 'features/incentive/helpers/formatEntryCount';
import { getWinnerAwardDisplay } from 'features/incentive/helpers/getWinnerAwardDisplay';
import {
  getIncentiveButtonAnalytics,
  IncentiveComponentNames,
} from 'features/incentive/helpers/incentiveAnalytics';
import useCurrentIncentives from 'features/incentive/hooks/useCurrentIncentives';
import { useIncentiveAnalytics } from 'features/incentive/hooks/useIncentiveAnalytics';
import useIncentiveProgress from 'features/incentive/hooks/useIncentiveProgress';
import useIncentiveStatus from 'features/incentive/hooks/useIncentiveStatus';
import useRefreshOnRefocus from 'features/incentive/hooks/useRefreshOnRefocus';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { VaroPressableNames } from 'shared/analytics/types/pressableNames';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import ProductOfferCard from 'shared/components/vxl/cards/ProductOfferCard/ProductOfferCard';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import useEvent from 'shared/hooks/useEvent/useEvent';
import theme from 'shared/styles/theme';

const PROMO_STATUSES = [
  IncentiveLifecycleStatus.Targeted,
  IncentiveLifecycleStatus.Eligible,
];
const TRACKED_STATUSES = [
  IncentiveLifecycleStatus.OptInPendingEligibility,
  IncentiveLifecycleStatus.Enrolled,
  IncentiveLifecycleStatus.InProgress,
];

type SweepstakesProgramCandidate = Omit<SweepstakesCardVariantProps, 'status'>;

interface RankedSweepstakesProgram extends SweepstakesCardVariantProps {
  rank: number;
}

const getCardRank = (status: IncentiveLifecycleStatus) => {
  if (status === IncentiveLifecycleStatus.Qualified) {
    return 3;
  }
  if (TRACKED_STATUSES.includes(status)) {
    return 2;
  }
  if (PROMO_STATUSES.includes(status)) {
    return 1;
  }
  return 0;
};

interface AccountCardContentProps {
  incentiveProgramId: string;
  status: IncentiveStatus;
  titleText: string;
  bodyText?: string;
  buttonText: string;
  illustration: IllustrationKeyType;
  illustrationBackgroundColor: string;
}

function AccountCardContent({
  incentiveProgramId,
  status,
  titleText,
  bodyText,
  buttonText,
  illustration,
  illustrationBackgroundColor,
}: AccountCardContentProps) {
  const router = useRouter();
  const { logComponentViewed } = useIncentiveAnalytics({
    incentiveType: IncentiveCategory.Sweepstakes,
    incentiveProgramId,
    lifecycleStatus: status.status,
  });

  const logCardViewed = useEvent(() => {
    logComponentViewed(IncentiveComponentNames.ACCOUNT_CARD);
  });

  useEffect(() => {
    logCardViewed();
  }, [logCardViewed]);

  const handlePress = () => {
    router.push({
      pathname: '/incentive/[programId]',
      params: { programId: incentiveProgramId },
    });
  };

  return (
    <AmplitudeMask style={styles.container}>
      <ProductOfferCard
        titleText={titleText}
        bodyText={bodyText}
        buttonText={buttonText}
        accessibilityLabel={[titleText, bodyText, buttonText]
          .filter(Boolean)
          .join(', ')}
        onPress={handlePress}
        illustrationKey={illustration}
        illustrationBackgroundColor={illustrationBackgroundColor}
        hasBorder
        componentAnalytics={getIncentiveButtonAnalytics(
          IncentiveCategory.Sweepstakes,
          VaroPressableNames.INCENTIVE.VIEW_INCENTIVE_FROM_ACCOUNT_CARD,
        )}
      />
    </AmplitudeMask>
  );
}

interface SweepstakesCardVariantProps {
  incentiveProgramId: string;
  status: IncentiveStatus;
  maxPrize: string;
  illustration: IllustrationKeyType;
}

function SweepstakesProgressCard({
  incentiveProgramId,
  status,
  illustration,
}: Omit<SweepstakesCardVariantProps, 'maxPrize'>) {
  const { t: translatedText } = useTranslation('incentive', {
    keyPrefix: 'sweepstakes.accountCard.progress',
  });
  const { progress, refresh } = useIncentiveProgress(incentiveProgramId);

  useRefreshOnRefocus(refresh);

  if (!progress) {
    return null;
  }

  return (
    <AccountCardContent
      incentiveProgramId={incentiveProgramId}
      status={status}
      titleText={translatedText('title', {
        chances: formatEntryCount(progress.cumulativeValue),
      })}
      bodyText={translatedText('body')}
      buttonText={translatedText('viewSweepstakes')}
      illustration={illustration}
      illustrationBackgroundColor={theme.color.expressive.orchid}
    />
  );
}

function SweepstakesStatusCard({
  incentiveProgramId,
  status,
  maxPrize,
  illustration,
}: SweepstakesCardVariantProps) {
  const { t: translatedText } = useTranslation('incentive', {
    keyPrefix: 'sweepstakes.accountCard',
  });

  if (PROMO_STATUSES.includes(status.status)) {
    return (
      <AccountCardContent
        incentiveProgramId={incentiveProgramId}
        status={status}
        titleText={translatedText('promo.title', { maxPrize })}
        buttonText={translatedText('promo.join')}
        illustration={illustration}
        illustrationBackgroundColor={theme.color.expressive.orchid}
      />
    );
  }

  if (TRACKED_STATUSES.includes(status.status)) {
    return (
      <SweepstakesProgressCard
        incentiveProgramId={incentiveProgramId}
        status={status}
        illustration={illustration}
      />
    );
  }

  if (status.status === IncentiveLifecycleStatus.Qualified) {
    const award = getWinnerAwardDisplay(status.awards);

    return (
      <AccountCardContent
        incentiveProgramId={incentiveProgramId}
        status={status}
        titleText={
          award
            ? translatedText('winner.title', { amount: award.amount })
            : translatedText('winner.titleWithoutAmount')
        }
        bodyText={translatedText('winner.body')}
        buttonText={translatedText('winner.prizeDetails')}
        illustration={illustration}
        illustrationBackgroundColor={theme.color.expressive.light}
      />
    );
  }

  return null;
}

function SweepstakesProgramStatus({
  candidate,
  remainingCandidates,
  selected,
}: {
  candidate: SweepstakesProgramCandidate;
  remainingCandidates: SweepstakesProgramCandidate[];
  selected: RankedSweepstakesProgram | undefined;
}) {
  const { status, fetching, refresh } = useIncentiveStatus(
    candidate.incentiveProgramId,
  );

  useRefreshOnRefocus(refresh);

  if (!status && fetching) {
    return null;
  }

  const rank = status ? getCardRank(status.status) : 0;
  const nextSelected =
    status && rank > (selected?.rank ?? 0)
      ? { ...candidate, status, rank }
      : selected;

  return (
    <SweepstakesProgramSelector
      candidates={remainingCandidates}
      selected={nextSelected}
    />
  );
}

function SweepstakesProgramSelector({
  candidates,
  selected,
}: {
  candidates: SweepstakesProgramCandidate[];
  selected?: RankedSweepstakesProgram;
}) {
  const [candidate, ...remainingCandidates] = candidates;

  if (candidate) {
    return (
      <SweepstakesProgramStatus
        candidate={candidate}
        remainingCandidates={remainingCandidates}
        selected={selected}
      />
    );
  }

  return selected ? (
    <SweepstakesStatusCard
      incentiveProgramId={selected.incentiveProgramId}
      status={selected.status}
      maxPrize={selected.maxPrize}
      illustration={selected.illustration}
    />
  ) : null;
}

function SweepstakesAccountCard() {
  const { incentives } = useCurrentIncentives(IncentiveCategory.Sweepstakes);
  const candidates = (incentives ?? []).flatMap((program) => {
    const content = resolveIncentiveContent(program);

    return content
      ? [
          {
            incentiveProgramId: program.id,
            maxPrize: content.content.max_prize_display,
            illustration: content.content.illustration,
          },
        ]
      : [];
  });

  return <SweepstakesProgramSelector candidates={candidates} />;
}

const styles = StyleSheet.create({
  container: {
    marginHorizontal: theme.spacing.m,
    marginVertical: theme.spacing.m,
  },
});

export default SweepstakesAccountCard;
